import React, { useState } from "react";
import moment from "moment";

import { apiService } from "src/services";
import { toast } from "src/components/Toast/ToastManager";
import Button from "src/components/Button/Button";

import styles from "./CancelRegistrationModal.module.scss";

export interface ICancelRegistrationEntry {
  show_registration_id: string;
  call_name: string;
  show_type: string;
  show_name: string;
  show_date: string;
  show_element: string;
  level?: string;
  section?: string;
  entry_fee: number | null;
  entry_platform_fee: number | null;
  transaction_id?: string | null;
  stripe_payment_intent_id?: string | null;
}

interface IProps {
  entry: ICancelRegistrationEntry;
  onSuccess(): void;
  onCancel(): void;
}

const CancelRegistrationModal: React.FC<IProps> = ({ entry, onSuccess, onCancel }) => {
  const [submitting, setSubmitting] = useState(false);

  const entryFee = entry.entry_fee ?? 0;
  const platformFee = entry.entry_platform_fee ?? 0;
  const totalRefund = Math.round((entryFee + platformFee) * 100) / 100;

  const hasAmounts = entry.entry_fee != null;

  const formatCurrency = (amount: number) =>
    amount.toLocaleString("en-US", { style: "currency", currency: "USD" });

  const handleConfirm = async () => {
    setSubmitting(true);
    try {
      const result = await apiService.cancelUserRegistration({
        show_registration_id: entry.show_registration_id,
        show_element: entry.show_element,
        level: entry.level,
        section: entry.section,
      });

      if (result.pending) {
        toast.show({
          title: "Refund Request Submitted",
          content: `${entry.call_name}'s cancellation request for ${entry.show_type} ${entry.show_name} has been submitted for approval. You'll be notified once it's reviewed.`,
          duration: 10000,
          type: "success",
        });
      } else {
        toast.show({
          title: "Registration Cancelled",
          content: `${entry.call_name}'s registration for ${entry.show_type} ${entry.show_name} has been cancelled. A refund will be issued to your original payment method.`,
          duration: 10000,
          type: "success",
        });
      }
      onSuccess();
    } catch (e: any) {
      toast.show({
        title: "Cancellation Failed",
        content: e?.response?.data?.message ?? "Something went wrong. Please try again later.",
        duration: 10000,
        type: "fail",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={styles.CancelRegistrationModal}>
      <div className={styles.header}>Cancel Registration</div>

      <div className={styles.entryDetails}>
        <div className={styles.row}>
          <span className={styles.label}>Dog</span>
          <span className={styles.value}>{entry.call_name}</span>
        </div>
        <div className={styles.row}>
          <span className={styles.label}>Show/Trial/Test</span>
          <span className={styles.value}>{entry.show_type} — {entry.show_name}</span>
        </div>
        <div className={styles.row}>
          <span className={styles.label}>Date</span>
          <span className={styles.value}>{moment(entry.show_date).format("MMMM Do, YYYY")}</span>
        </div>
        <div className={styles.row}>
          <span className={styles.label}>Class</span>
          <span className={styles.value}>
            {entry.show_element}
            {entry.level ? ` — ${entry.level}` : ""}
            {entry.section ? ` (${entry.section})` : ""}
          </span>
        </div>
      </div>

      <div className={styles.refundBreakdown}>
        <div className={styles.refundTitle}>
          Refund Summary{entry.stripe_payment_intent_id ? ` (${entry.stripe_payment_intent_id})` : ""}
        </div>
        <div className={styles.refundRow}>
          <span>Entry Fee</span>
          <span>{hasAmounts ? formatCurrency(entryFee) : "—"}</span>
        </div>
        <div className={styles.refundRow}>
          <span>Platform Fee</span>
          <span>{hasAmounts ? formatCurrency(platformFee) : "—"}</span>
        </div>
        <div className={`${styles.refundRow} ${styles.total}`}>
          <span>Total Refund</span>
          <span>{hasAmounts ? formatCurrency(totalRefund) : "—"}</span>
        </div>
        {!hasAmounts && (
          <p className={styles.feeNote}>Refund amount will be calculated at the time of cancellation.</p>
        )}
        <p className={styles.feeNote}>
          Refunds may take 5–10 business days to appear on your statement.
        </p>
      </div>

      <p className={styles.warning}>
        Are you sure you want to cancel this registration? This action cannot be undone. If the event requires approval, your refund will be processed once reviewed.
      </p>

      <div className={styles.actions}>
        <Button onClick={onCancel} disabled={submitting}>
          Keep Registration
        </Button>
        <Button isLoading={submitting} onClick={handleConfirm}>
          Cancel &amp; Request Refund
        </Button>
      </div>
    </div>
  );
};

export default CancelRegistrationModal;
